import { Add, DeleteOutline, Edit } from '@mui/icons-material';
import { IconButton, Link, Stack, TextField, Typography } from '@mui/material';
import { useState } from 'react';
import {
    useMutateCreateCropMonitoringNote,
    useMutateDeleteCropMonitoringNote,
    useMutateUpdateCropMonitoringNote,
} from '/@/hooks/crop-monitoring-notes';
import { useOwnUserNode } from '/@/hooks/nodes';
import { _ } from '/@/locale';
import type { FieldRegistryRecordOrigin, FieldRegistryType } from '/@/store/tracking/events/field-registry';
import type { CropMonitoring } from '/@/types/crop-monitoring';
import type { CropMonitoringNote } from '/@/types/crop-monitoring-note';
import { formatDate } from '/@/utils';
import { trackRegistryChange } from './add/flow-tracking';
import { EditActions } from './edit-actions';

const RegistryNote = ({ note }: { note: CropMonitoringNote }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [content, setContent] = useState(note.content ?? '');
    const updateNote = useMutateUpdateCropMonitoringNote();
    const deleteNote = useMutateDeleteCropMonitoringNote();
    const ownUserNodeQuery = useOwnUserNode();

    const authorName = [note.createdBy?.user?.firstName, note.createdBy?.user?.lastName]
        .filter(Boolean)
        .join(' ')
        .trim();

    const handleSave = async () => {
        if (!content.trim()) return;
        await updateNote.trigger({ id: note.id, content: content.trim() });
        setIsEditing(false);
    };

    return (
        <Stack spacing={1} sx={{ bgcolor: 'grey.100', p: 1, overflow: 'auto', width: '100%' }}>
            {isEditing ? (
                <>
                    <TextField
                        fullWidth
                        multiline
                        value={content}
                        onChange={(event) => setContent(event.target.value)}
                        size='small'
                    />
                    <EditActions
                        onCancel={() => {
                            setContent(note.content ?? '');
                            setIsEditing(false);
                        }}
                        onSave={handleSave}
                        saveDisabled={!content.trim() || updateNote.isMutating}
                        cancelVariant='text'
                        size='small'
                    />
                </>
            ) : (
                <Stack direction='row' alignItems='flex-start' justifyContent='space-between' spacing={1}>
                    <Stack>
                        <Typography variant='caption' fontWeight='bold' color='primary'>
                            {`${authorName || _('Registrado')} - ${formatDate(new Date(note.created))}`}
                        </Typography>
                        <Typography variant='caption' color='primary'>
                            {note.content}
                        </Typography>
                    </Stack>

                    <Stack direction='row' alignItems='center'>
                        <IconButton
                            size='small'
                            aria-label={_('Editar')}
                            onClick={() => setIsEditing(true)}
                            disabled={!(ownUserNodeQuery?.data?.id === note.createdBy?.id)}
                        >
                            <Edit fontSize='small' />
                        </IconButton>
                        <IconButton
                            disabled={!(ownUserNodeQuery?.data?.id === note.createdBy?.id) || deleteNote.isMutating}
                            size='small'
                            aria-label={_('Eliminar')}
                            onClick={() => deleteNote.trigger({ id: note.id })}
                        >
                            <DeleteOutline fontSize='small' />
                        </IconButton>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
};

const RegistryNotes = ({
    notes,
    fieldId,
    applicationRegisterId,
    applicationReportResultId,
    plantingDateCropMonitoringId,
    cornYieldEstimationId,
    claimId,
    closedCropMonitoringId,
    registryType,
    registryOrigin,
    cropMonitoring,
}: {
    notes: CropMonitoringNote[];
    fieldId: number;
    /** The registry the notes belong to, for its `Field Registry Note Added` event. */
    registryType: FieldRegistryType;
    registryOrigin: FieldRegistryRecordOrigin;
    cropMonitoring?: CropMonitoring;
    /** Attach new notes to a manual application register… */
    applicationRegisterId?: number;
    /** …or to a FieldView application report result… */
    applicationReportResultId?: number;
    /** …or to a planting (crop monitoring) record… */
    plantingDateCropMonitoringId?: number;
    /** …or to a yield estimation… */
    cornYieldEstimationId?: number;
    /** …or to a claim… */
    claimId?: number;
    /** …or to a closed (harvested) crop monitoring. Exactly one is provided. */
    closedCropMonitoringId?: number;
}) => {
    const [isAdding, setIsAdding] = useState(false);
    const [draft, setDraft] = useState('');
    const createNote = useMutateCreateCropMonitoringNote();

    const handleAdd = async () => {
        if (!draft.trim()) return;
        await createNote.trigger({
            field: fieldId,
            applicationRegister: applicationRegisterId,
            applicationReportResult: applicationReportResultId,
            plantingDateCropMonitoring: plantingDateCropMonitoringId,
            cornYieldEstimation: cornYieldEstimationId,
            claim: claimId,
            closedCropMonitoring: closedCropMonitoringId,
            content: draft.trim(),
        });
        trackRegistryChange('Field Registry Note Added', { fieldId, cropMonitoring, registryType, registryOrigin });
        setDraft('');
        setIsAdding(false);
    };

    return (
        <Stack spacing={1}>
            {notes.map((note) => (
                <RegistryNote key={note.id} note={note} />
            ))}

            {isAdding ? (
                <Stack spacing={1}>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder={_('Escribir una observación')}
                        value={draft}
                        onChange={(event) => setDraft(event.target.value)}
                    />

                    <EditActions
                        onCancel={() => {
                            setDraft('');
                            setIsAdding(false);
                        }}
                        onSave={handleAdd}
                        saveDisabled={!draft.trim() || createNote.isMutating}
                        cancelVariant='text'
                        size='small'
                    />
                </Stack>
            ) : (
                <Link
                    component='button'
                    variant='caption'
                    color='primary'
                    underline='always'
                    onClick={() => setIsAdding(true)}
                    sx={{ alignSelf: 'flex-start', display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                >
                    <Add sx={{ fontSize: 14 }} />
                    {_('Agregar una nota')}
                </Link>
            )}
        </Stack>
    );
};

export default RegistryNotes;
